import {
	Button,
	Checkbox,
	TableBody,
	TableContainer,
	TableHead,
	Typography,
	useTheme,
} from '@mui/material';
import {format, parse} from 'date-fns';
import {ptBR} from 'date-fns/locale';
import React from 'react';

import {TagChip} from 'presentation/components';
import {translations} from 'presentation/translations-new';
import {formatPriceBill} from 'presentation/utils';
import {useOpenBillsTable} from './hooks/useOpenBillsTable';
import {Card, StyledTable, StyledTableBodyRow, StyledTableCell, StyledTableHeadRow} from './styles';
import type {OpenBillRow, OpenBillsTableProps} from './types';
import {OpenBillStatusEnum} from './types';

const {interestFinesV2} = translations['pt-br'].privateFlow.myFinancial;
const {status, value, dueDate, reference, viewDetails} =
	translations['pt-br'].privateFlow.myFinancial.openBills.openBillsTable;

function OpenBillsTable({
	rows,
	selectedIds: externalSelectedIds,
	onSelectionChange,
	onViewDetails,
}: OpenBillsTableProps) {
	const {palette} = useTheme();
	const {
		selectedIds,
		isAllSelected,
		isSomeSelected,
		handleSelectAll,
		handleSelectRow,
		isRowSelectable,
		handleViewDetails,
	} = useOpenBillsTable(
		rows,
		onSelectionChange,
		externalSelectedIds,
		onViewDetails,
	);

	return (
		<Card id="open-bills-table">
			<TableContainer>
				<StyledTable>
					<TableHead>
						<StyledTableHeadRow>
							<StyledTableCell padding="checkbox">
								<Checkbox
									indeterminate={isSomeSelected && !isAllSelected}
									checked={isAllSelected}
									onChange={handleSelectAll}
								/>
							</StyledTableCell>
							<StyledTableCell>{status}</StyledTableCell>
							<StyledTableCell>{value}</StyledTableCell>
							<StyledTableCell>{dueDate}</StyledTableCell>
							<StyledTableCell>{reference}</StyledTableCell>
							<StyledTableCell align="right" />
						</StyledTableHeadRow>
					</TableHead>
					<TableBody>
						{rows.map((row: OpenBillRow, index: number) => (
							<StyledTableBodyRow key={row.cdObrigacao}>
								<StyledTableCell padding="checkbox">
									<Checkbox
										id={
											index === 0
												? 'open-bills-first-row-checkbox'
												: undefined
										}
										checked={selectedIds.has(row.cdObrigacao)}
										disabled={!isRowSelectable(row)}
										onChange={() => handleSelectRow(row.cdObrigacao)}
									/>
								</StyledTableCell>
								<StyledTableCell>
									<TagChip
										label={row.status}
										color={
											row.status === OpenBillStatusEnum.OVERDUE
												? 'error'
												: 'info'
										}
									/>
								</StyledTableCell>
								<StyledTableCell>
									<>
										<Typography variant="h6" fontWeight={600} component="span">
											{formatPriceBill(row.vlAtualizado)}
										</Typography>
										{row.status === OpenBillStatusEnum.OVERDUE && (
											<Typography
												variant="h6"
												component="span"
												color={
													(palette as {text?: {error?: string}}).text
														?.error ?? palette.error.main
												}
												fontWeight={600}>
												*
											</Typography>
										)}
									</>
								</StyledTableCell>
								<StyledTableCell>{row.dtVencimento}</StyledTableCell>
								<StyledTableCell>
									{format(
										parse(row.dtVencimento, 'dd/MM/yyyy', new Date()),
										'MMM/yyyy',
										{locale: ptBR},
									).replace(/^\w/, c => c.toUpperCase())}
								</StyledTableCell>
								<StyledTableCell align="right">
									<Button
										type="button"
										variant="outlined"
										size="small"
										sx={{borderRadius: 9999}}
										onClick={() => handleViewDetails(row)}>
										{viewDetails}
									</Button>
								</StyledTableCell>
							</StyledTableBodyRow>
						))}
					</TableBody>
				</StyledTable>
			</TableContainer>
			{rows.some(r => r.status === OpenBillStatusEnum.OVERDUE) ? (
				<Typography variant="caption" color="text.secondary">
					{interestFinesV2}
				</Typography>
			) : null}
		</Card>
	);
}

export {OpenBillsTable};
